import { defineStore } from 'pinia';
import { client, getErrorMessage } from '@/api';
import { ElMessage } from 'element-plus';
import { CommentsBlockSettingDto } from '@/api/models/comments-block-setting.dto';
import { GetUserBlockSettingParameters } from '@/api/models/get-user-block-setting-parameters';
import { UpdateUserBlockSettingParameters } from '@/api/models/update-user-block-setting-parameters';

export type UserBlockState = {
  loading: boolean;
  error: string | null;
  settings: CommentsBlockSettingDto | null;
};

const getInitState = () => ({
  loading: false,
  error: null,
  settings: null,
});

const SHORTCUTS = [
  {
    text: 'Today',
    value: new Date(),
  },
  {
    text: 'For 1 day',
    value: () => {
      const date = new Date();

      date.setDate(date.getDate() + 1);

      return date;
    },
  },
  {
    text: 'For 3 days',
    value: () => {
      const date = new Date();

      date.setDate(date.getDate() + 3);

      return date;
    },
  },
  {
    text: 'For 15 days',
    value: () => {
      const date = new Date();

      date.setDate(date.getDate() + 15);

      return date;
    },
  },
  {
    text: 'For 30 days',
    value: () => {
      const date = new Date();

      date.setDate(date.getDate() + 30);

      return date;
    },
  },
  {
    text: 'For 100 years',
    value: () => {
      const date = new Date();

      date.setFullYear(date.getFullYear() + 100);

      return date;
    },
  },
];

export const useUserBlock = defineStore('user-block', {
  state: (): UserBlockState => getInitState(),

  actions: {
    reset() {
      this.$reset();
    },

    setLoading(value: boolean) { this.loading = value; },
    setError(value: string | null) { this.error = value; },

    setSettings(settings: CommentsBlockSettingDto | null) {
      this.settings = (settings === null) ? null : { ...this.settings, ...settings };
    },

    getShortcuts() {
      return SHORTCUTS;
    },

    async getUserBlockSetting(query: GetUserBlockSettingParameters): Promise<CommentsBlockSettingDto> {
      this.setError(null);
      this.setLoading(true);

      let settings: CommentsBlockSettingDto = { moderator: '' };

      try {
        settings = await client.userBlockApi.getUserBlockSetting(query);

        this.setSettings(settings);
      } catch (err) {
        const message = getErrorMessage(err, 'Error during getting user block settings');

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return settings;
    },

    async updateUserBlockSetting(
      query: UpdateUserBlockSettingParameters,
      data: CommentsBlockSettingDto,
    ): Promise<Record<string, any>> {
      this.setError(null);
      this.setLoading(true);

      try {
        await client.userBlockApi.updateUserBlockSetting(query, data);

        ElMessage.success({
          message: 'User block settings was updated successfully',
          duration: 10000,
        });
      } catch (err) {
        ElMessage.error({
          message: getErrorMessage(err, 'Error during updating user block settings'),
          duration: 10000,
        });
      } finally {
        this.setLoading(false);
      }

      return {};
    },
  },
});
